/** Versioned capabilities, not inferred from DOM text or model output. */
export const catalog = {
  'whatsapp.rename': {
    version: 1, status: 'available', label: 'Etichetta del numero WhatsApp',
    elementId: 'whatsapp.number.label',
    explanation: 'Un nome interno aiuta i colleghi a riconoscere il numero. Non cambia il nome pubblico su WhatsApp.',
    source: 'src/components/settings/whatsapp/WhatsAppNumberActions.tsx',
  },
  'whatsapp.shift-routing': {
    version: 1, status: 'unsupported', label: 'Assegnazione per turni',
    explanation: 'Il pannello Routing & Team è ancora un segnaposto: non posso configurarlo da qui.',
    source: 'src/components/settings/whatsapp/WhatsAppSettingsHub.tsx',
  },
} as const;

export type Scope = Readonly<{ organizationId: string; userId: string; configurationId: string }>;
export interface RenamePort {
  /** Recheck identity + tenant + settings_whatsapp edit permission. Backend RLS remains mandatory. */
  authorize(scope: Scope): Promise<boolean>;
  read(scope: Scope): Promise<string | null>;
  compareAndSet(scope: Scope, before: string | null, after: string): Promise<boolean>;
}
export type Proposal = Readonly<{
  id: string; capability: 'whatsapp.rename'; version: 1; scope: Scope;
  before: string | null; after: string; expiresAt: number;
}>;
export type Result = { status: 'verified' | 'conflict' | 'unknown' | 'denied' | 'expired' | 'invalid' | 'cancelled' };
const scopeKey = (s: Scope) => JSON.stringify([s.organizationId, s.userId, s.configurationId]);
const validScope = (s: Scope) => [s.organizationId, s.userId, s.configurationId].every(v => typeof v === 'string' && v.length > 0);

/** Local confirmation ledger; never expose confirm() as an LLM tool. Not durable across reloads. */
export function createConsultant(port: RenamePort, now = Date.now, id: () => string = () => crypto.randomUUID()) {
  const pending = new Map<string, Proposal>();
  const completed = new Map<string, Result>();
  const running = new Map<string, Promise<Result>>();
  return {
    async propose(scope: Scope, rawName: string): Promise<Proposal> {
      const after = rawName.trim();
      if (!validScope(scope) || !after || after.length > 80 || /[\u0000-\u001f\u007f]/.test(after)) throw new Error('Nome o contesto non valido');
      if (!await port.authorize(scope)) throw new Error('Permesso non disponibile');
      const before = await port.read(scope);
      const proposal: Proposal = Object.freeze({ id: id(), capability: 'whatsapp.rename', version: 1,
        scope: Object.freeze({ ...scope }), before, after, expiresAt: now() + 5 * 60_000 });
      pending.set(proposal.id, proposal);
      return proposal;
    },
    cancel(proposalId: string): boolean {
      if (!pending.delete(proposalId)) return false;
      completed.set(proposalId, { status: 'cancelled' });
      return true;
    },
    confirm(proposalId: string, currentScope: Scope): Promise<Result> {
      // The trusted UI passes only an opaque proposal ID; model-supplied payloads cannot replace values.
      if (running.has(proposalId)) return running.get(proposalId)!;
      if (completed.has(proposalId)) return Promise.resolve(completed.get(proposalId)!);
      const p = pending.get(proposalId);
      if (!p) return Promise.resolve({ status: 'invalid' });
      pending.delete(proposalId);
      const execute = async (): Promise<Result> => {
        if (scopeKey(p.scope) !== scopeKey(currentScope)) return { status: 'denied' };
        if (now() >= p.expiresAt) return { status: 'expired' };
        try {
          if (!await port.authorize(p.scope)) return { status: 'denied' };
          if (await port.read(p.scope) !== p.before) return { status: 'conflict' };
          if (p.before !== p.after && !await port.compareAndSet(p.scope, p.before, p.after)) return { status: 'conflict' };
          // Even a successful UPDATE is not proof: independently reread the same scoped setting.
          if (!await port.authorize(p.scope)) return { status: 'unknown' };
          return { status: await port.read(p.scope) === p.after ? 'verified' : 'unknown' };
        } catch { return { status: 'unknown' }; }
      };
      const promise = execute().then(result => { completed.set(proposalId, result); running.delete(proposalId); return result; });
      running.set(proposalId, promise);
      return promise;
    },
  };
}

/** Only structural metadata goes to the future model. Never DOM contents, customer records or config JSON. */
export function screenContext(screen: string) {
  if (screen !== 'settings.whatsapp') return { screen: 'unknown', elements: [], capabilities: [] };
  return { screen, elements: [{ id: 'whatsapp.number.label', label: 'Etichetta interna numero', kind: 'text-setting' }],
    capabilities: Object.entries(catalog).map(([id, c]) => ({ id, version: c.version, status: c.status, explanation: c.explanation })) };
}

export function prepareTeamDraft(need: string, desiredOutcome: string) {
  if (!need.trim() || !desiredOutcome.trim()) throw new Error('Descrivi esigenza e risultato');
  return { status: 'draft' as const, type: 'feature' as const,
    description: `Esigenza: ${need.trim()}\n\nRisultato atteso: ${desiredOutcome.trim()}\n\nContesto: Impostazioni > WhatsApp`,
    destination: 'Team Flusia', sent: false as const };
}
